import { makeAutoObservable, observable, action } from "mobx";
import { createContext, useContext } from "react";

export default class ViewState {
  userName = "";
  mainDrawerOpen = true;
  saveDialogOpen = false;
  newProjectDialogOpen = false;
  loadProjectDialogOpen = false;
  aboutDialogOpen = false;
  selectedTaskId = "";
  selectedTaskIndex = "";
  projectAnalysis = "";
  selectedDistributionId = "";
  lastTaskTreeExpansion = ["0"];
  lastAnalysisTreeExpansion = ["0"];
  activeBaseline = [];
  archiveBaseline = [];
  activeProject = [];
  archiveProject = [];
  showAnalysisLoadingMessage = true;
  analysisTabViewMode = 'table';
  analysisTableLoading = false;
  taskViewSelectedTab = "actual";

  constructor() {
    makeAutoObservable(this);
  }

  setSelectedTaskIndex(id) {
    this.selectedTaskIndex = id;
  }

  setSelectedTaskId(id) {
    this.selectedTaskId = id;
  }

  setMainDrawerState(state) {
    this.mainDrawerOpen = state;
  }

  setUsername(name) {
    this.userName = name;
  }

  setSaveDialogState(state) {
    this.saveDialogOpen = state;
  }

  setAboutDialogState(state) {
    this.aboutDialogOpen = state;
  }

  setNewProjectDialogState(state) {
    this.newProjectDialogOpen = state;
  }

  setLoadProjectDialogState(state) {
    this.loadProjectDialogOpen = state;
  }

  setSaveDialogState(state) {
    this.saveDialogOpen = state;
  }

  setProjectAnalysis(projAnalysis) {
    this.projectAnalysis = projAnalysis;
  }

  setBaselines(baselines) {
    this.archiveBaseline = baselines.filter((x) => x.isArchived);
    this.activeBaseline = baselines.filter((x) => !x.isArchived);
  }

  setTaskViewSelectedTab = action((value) => this.taskViewSelectedTab = value);

  toJS() {
    let plainObject = {};
    plainObject.userName = this.userName;
    plainObject.mainDrawerOpen = this.mainDrawerOpen;
    plainObject.aboutDialogOpen = this.aboutDialogOpen;
    plainObject.saveDialogOpen = this.saveDialogOpen;
    plainObject.loadProjectDialogOpen = this.loadProjectDialogOpen;
    plainObject.createNewProjectDialogOpen = this.newProjectDialogOpen;
    plainObject.selectedTaskid = this.selectedTaskId;
    plainObject.selectedTaskIndex = this.selectedTaskIndex;
    plainObject.selectedDistributionId = this.selectedDistributionId;
    plainObject.lastAnalysisTreeExpansion = this.lastAnalysisTreeExpansion;
    plainObject.lastTaskTreeExpansion = this.lastTaskTreeExpansion;
    plainObject.activeBaseline = this.activeBaseline;
    plainObject.archiveBaseline = this.archiveBaseline;
    plainObject.activeProject = this.activeProject;
    plainObject.archiveProject = this.archiveProject;
    plainObject.showAnalysisLoadingMessage = this.showAnalysisLoadingMessage;
    plainObject.analysisTabViewMode = this.analysisTabViewMode;
    plainObject.analysisTableLoading  = this.analysisTableLoading;
    plainObject.taskViewSelectedTab = this.taskViewSelectedTab;
    return plainObject;
  }

  static fromJS(savedState) {
    const viewState = new ViewState();
    if (savedState != null) {
      viewState.userName = savedState.userName;
      viewState.mainDrawerOpen = savedState.mainDrawerOpen;
      viewState.saveDialogOpen = savedState.saveDialogOpen;
      viewState.aboutDialogOpen = savedState.aboutDialogOpen;
      viewState.newProjectDialogOpen = savedState.createNewProjectDialogOpen;
      viewState.loadProjectDialogOpen = savedState.loadProjectDialogOpen;
      viewState.selectedTaskId = savedState.selectedTaskId;
      viewState.selectedTaskIndex = savedState.selectedTaskIndex;
      viewState.selectedDistributionId = savedState.selectedDistributionId;
      viewState.lastAnalysisTreeExpansion =
        savedState.lastAnalysisTreeExpansion;
      viewState.lastTaskTreeExpansion = savedState.lastTaskTreeExpansion;
      viewState.activeBaseline = savedState.activeBaseline;
      viewState.archiveBaseline = savedState.archiveBaseline;
      viewState.activeProject = savedState.activeProject;
      viewState.archiveProject = savedState.archiveProject;
      viewState.showAnalysisLoadingMessage = savedState.showAnalysisLoadingMessage;
      viewState.analysisTabViewMode = savedState.analysisTabViewMode;
      viewState.analysisTableLoading  = savedState.analysisTableLoading;
      viewState.taskViewSelectedTab = savedState.taskViewSelectedTab;
    }
    return viewState;
  }
}

const ViewStateContext = createContext(new ViewState());

export const ViewStateProvider = ({ children, state }) => {
  return (
    <ViewStateContext.Provider value={observable(state)}>{children}</ViewStateContext.Provider>
  );
};

/* Hook to use ViewState in any functional component */
export const useViewState = () => useContext(ViewStateContext);
